import Table from '@eduzz/houston-ui/Table';
import TagHighlight from '@eduzz/houston-ui/Tag/Highlight';
import { Link } from '@material-ui/core';
import React, { useState } from 'react';
import { sendEventToGA } from 'v2/helpers/analytics';
import { numberToBRL } from 'v2/helpers/money';
import { TransferStatus, friendilyAntifraudStatus, type TransferHistoryEntry } from 'v2/interfaces/financial';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { InitialPagination } from 'v2/services/transferHistory';

import type { IPaginationResponse } from 'v2/interfaces/pagination';

import useStyles from './styles';
import CompositionModal from '../CompositionModal';

interface IContentTableProps {
  data: TransferHistoryEntry[];
  pagination: IPaginationResponse;
  handlePagination: {
    onChangeRowsPerPage: (per_page: number) => void;
    onChangePage: (page: number) => void;
  };
  loading: boolean;
}

function CancelledTransferLinkKnowMore() {
  const classes = useStyles();

  return (
    <>
      <Link
        className={classes.linkButton}
        href='https://ajuda.eduzz.com/hc/pt-br/articles/6734291431067-Estou-com-problemas-nas-minhas-transfer%C3%AAncias-e-agora'
        target='_blank'
      >
        Clique aqui
      </Link>{' '}
      e saiba mais.
    </>
  );
}

function ContentTable({ data, pagination, handlePagination, loading }: IContentTableProps) {
  const classes = useStyles();

  const [openCompositionModal, setOpenCompositionModal] = useState<boolean>(false);
  const [currentCompositionId, setCurrentCompositionId] = useState<number | null>(null);

  const isBetaInvoiceTransfer = eduzzAccountsService.verifyUserHasBetaModule('transferencia_fatura');

  const formatDate = (dateRaw: string) => new Intl.DateTimeFormat('pt-BR').format(new Date(`${dateRaw}T00:00:00`));

  const handleToggleCompositionModal = () => setOpenCompositionModal((prevState) => !prevState);

  const handleClickCompositionReport = (compositionId: number) => {
    sendEventToGA({
      category: 'Novo Extrato',
      action: 'Botão',
      label: 'Composição de saque',
    });

    setCurrentCompositionId(compositionId);
    handleToggleCompositionModal();
  };

  const statusColor = (statusName: string) => {
    if (statusName === 'Cancelada') return 'feedbackColor.negative.pure';
    if (statusName === 'Confirmada') return 'feedbackColor.positive.pure';
    return 'neutralColor.low.dark';
  };

  function getDetails(entry: TransferHistoryEntry) {
    const { id, status_id, status_name, antifraud_status_name, can_export_transfer_data } = entry;

    const canViewTransferReport = can_export_transfer_data && status_id === TransferStatus.CONFIRMADA && !!isBetaInvoiceTransfer;

    if (status_name === 'Em Andamento') return 'Sua solicitação de transferência foi recebida e está em andamento.';
    if (status_name === 'Em Processamento') return 'A solicitação de transferência foi recebida e está em fase de processamento.';
    if (status_name === 'Confirmada' && canViewTransferReport) {
      return (
        <Link id={`composition-${id}`} className={classes.linkButton} onClick={() => handleClickCompositionReport(id)}>
          Composição de Saque
        </Link>
      );
    }
    if (status_name === 'Confirmada' && !canViewTransferReport) return 'Transferência realizada com sucesso.';
    if (status_name === 'Pendente') return 'O prazo para transferência bancária é de 1 dia útil.';

    if (status_name === 'Cancelada') {
      if (antifraud_status_name) {
        return (
          <>
            {friendilyAntifraudStatus[antifraud_status_name]}&nbsp;
            {antifraud_status_name !== 'Cancelado Solicitação do Cliente' && <CancelledTransferLinkKnowMore />}
          </>
        );
      }

      return (
        <>
          Falha na transferência.&nbsp;
          <CancelledTransferLinkKnowMore />
        </>
      );
    }

    return '';
  }

  return (
    <>
      <Table id='transfers-table' total={pagination?.total_items || 0} loading={loading}>
        {!loading && data?.length > 0 && (
          <Table.Header>
            <Table.Column width={180}>Data da solicitação</Table.Column>
            <Table.Column width={180}>Data de conclusão</Table.Column>
            <Table.Column width={140}>Valor</Table.Column>
            <Table.Column width={200} align='center'>
              Status
            </Table.Column>
            <Table.Column>Detalhes</Table.Column>
          </Table.Header>
        )}
        <Table.Body>
          {data?.map((entry: TransferHistoryEntry, index: number) => (
            <Table.Row data={entry} index={index} key={`TransferItem-${entry.id}`}>
              <Table.Cell>{formatDate(entry.solicitation_date)}</Table.Cell>
              <Table.Cell>{entry.conclusion_date ? formatDate(entry.conclusion_date) : ''}</Table.Cell>
              <Table.Cell>{numberToBRL(entry.value)}</Table.Cell>
              <Table.Cell align='center'>
                {
                  <TagHighlight variant='outlined' color={statusColor(entry.status_name)}>
                    {entry.status_name}
                  </TagHighlight>
                }
              </Table.Cell>
              <Table.Cell>{getDetails(entry)}</Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
        {!loading && (pagination?.total_pages > 1 || pagination?.per_page > InitialPagination.per_page) && (
          <Table.Pagination
            className={classes.paginationSelect}
            page={pagination?.current_page}
            perPage={pagination?.per_page}
            onChangePage={handlePagination.onChangePage}
            onChangePerPage={handlePagination.onChangeRowsPerPage}
            optionsPerPage='15,30,50,100'
          />
        )}
      </Table>
      <CompositionModal open={openCompositionModal} setClose={handleToggleCompositionModal} compositionId={currentCompositionId} />
    </>
  );
}

export default ContentTable;
